import React, { useCallback, useContext, useState } from 'react';
import ChatBubbleIcon from '@mui/icons-material/ChatBubble';
import { Button } from 'components/baseComponents/Button';
import { Typography, Input, Icon } from 'gd-design-library';
import { ChatbotWidgetContext } from 'components/ChatbotWidget/utils/ChatbotWidgetContext';
import { View } from 'components/ChatbotWidget/constants/view';
import styles from './ChatSignIn.module.scss';
import classNames from 'classnames/bind';
import { useStartChat } from 'components/ChatbotWidget/hooks/useStartChat';

const cx = classNames.bind(styles);

const CN = 'chat-sign-in';
const headerClassName = `${CN}__header`;
const backButtonClassName = `${CN}__back-button`;

interface Errors {
  email?: string;
  name?: string;
}

export const ChatSignIn = (): React.ReactElement => {
  const [ errors, setErrors ] = useState<Errors>({});
  const [ isChatLoading, setIsChatLoading ] = useState(false);
  const startChat = useStartChat();

  const { setView, setClient, options, user } = useContext(ChatbotWidgetContext);
  const { name, setName, email, setEmail } = user;

  const signIn = useCallback(async () => {
    const currentErrors: Errors = {};

    if (!name.trim()) {
      currentErrors.name = 'Please enter your name';
    } else {
      currentErrors.name = undefined;
    }

    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      currentErrors.email = 'Please enter a valid email address';
    } else {
      currentErrors.email = undefined;
    }

    setErrors(currentErrors);

    if (!currentErrors.email && !currentErrors.name) {
      setIsChatLoading(true);
      startChat();
    }
  }, [ name, email, options, setClient ]);

  return (
    <div className={ cx(CN) }>
      <div className={ cx(headerClassName) }>
        <div>
          <Button
            iconStart={ <Icon name='arrowLeft' /> }
            onClick={ (): void => setView(View.SELECT_CONTACT_OPTION) }
            variant='text'
            className={ cx(backButtonClassName) }
          >
            Back
          </Button>
        </div>
        <Typography variant='h4' align='center'>Chat with Us</Typography>
      </div>
      <Input
        variant='text'
        label='Name'
        name='name'
        autocomplete='name'
        value={ name }
        onChange={ (e): void => setName(e.target.value) }
        color={ errors.name ? 'error' : 'primary' }
        helperText={ errors.name }
      />
      <Input
        variant='email'
        label='Email'
        name='email'
        autocomplete='email'
        value={ email }
        onChange={ (e): void => setEmail(e.target.value) }
        color={ errors.email ? 'error' : 'primary' }
        helperText={ errors.email }
      />
      <Button
        variant='primary'
        onClick={ signIn }
        loading={ isChatLoading }
        iconStart={ <ChatBubbleIcon /> }
      >
        {isChatLoading ? 'Starting Chat...' : 'Start Chat'}
      </Button>
    </div>
  );
};
